import React from "react";
import { Modal, ModalContent, ModalBody, ModalFooter, Button, Input } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useStyles } from "@/hooks/useStyles";
import DataTable from "components/common/table/DataTable";

import { IoSearchSharp } from "react-icons/io5";
import useAxiosPrivate from "@/hooks/useAxiosPrivate";
import { useQuery } from "@tanstack/react-query";

type CustomerType = {
  customer_group_id: string;
  customer_group_alias: string;
  status_automation: string;
};

export default function CustomerGroupListModal({ isOpen, onClose, onSubmit }) {
  const { t } = useTranslation();
  const styles = useStyles();
  const [filterValue, setFilterValue] = React.useState("");
  const [selectedCustomers, setSelectedCustomers] = React.useState<CustomerType[]>([]);

  const columns = [
    { name: "ID", uid: "customer_group_id", sortable: true, style: { width: "200px" } },
    { name: "Alias", uid: "customer_group_alias", sortable: true },
  ];

  const axiosPrivate = useAxiosPrivate();

  const {
    data: apiResponse,
  } = useQuery<{ data: CustomerType[]; pagination: any }>({
    queryKey: ["CustomerGroupSettings"],
    queryFn: async () => {
      const response = await axiosPrivate.get(`/customers_group/settings?per_page=1000`);
      return response.data;
    },
    staleTime: 6000000,
    gcTime: 6000000,
  });

  const customersList = React.useMemo(() => apiResponse?.data ?? [], [apiResponse]);

  const filteredCustomers = React.useMemo(() => {
    let filtered = [...customersList];

    if (filterValue) {
      filtered = filtered.filter(
        (customer) =>
          customer.customer_group_alias
            .toLowerCase()
            .includes(filterValue.toLowerCase()) ||
          customer.customer_group_id
            .toLowerCase()
            .includes(filterValue.toLowerCase()),
      );
    }

    return filtered;
  }, [filterValue, customersList]);

  const handleSubmit = () => {
    const selectedIds = selectedCustomers.map((customer) => customer.customer_group_id);
    onSubmit(selectedIds);
    onClose();
  };

  const renderCell = (customer: CustomerType, columnKey: React.Key) => {
    const cellValue = customer[columnKey as keyof CustomerType];
    return cellValue;
  };

  const topContent = (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center">
        <div className="flex gap-3">
          <Input
            isClearable
            classNames={{
              base: "w-full min-w-[75%]",
              inputWrapper: [
                "border-0",
                "bg-white",
                "h-9",
                "min-h-unit-9",
                "hover:bg-white",
                "group-data-[hover=true]:bg-white",
                "group-data-[focus=true]:bg-white",
              ],
              input: "text-vistagrey",
            }}
            placeholder={t("search")}
            size="md"
            startContent={<IoSearchSharp className="text-default-300" />}
            value={filterValue}
            onClear={() => setFilterValue("")}
            onValueChange={setFilterValue}
          />
        </div>
      </div>
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      backdrop="opaque"
      placement="top-center"
      size="5xl"
      classNames={{
        backdrop: "bg-gradient-to-t from-vistadark/80 to-vistadark/60 backdrop-opacity-20",
        base: "max-h-[80vh]",
        body:" overflow-y-auto"
      }}
    >
      <ModalContent>
        <ModalBody>
          <div className="pt-5">
            <DataTable<CustomerType>
              columns={columns}
              data={filteredCustomers || []}
              renderCell={renderCell}
              keyExtractor={(customer) => customer.customer_group_id}
              initialSortDescriptor={{ column: "id", direction: "ascending" }}
              topContent={topContent}
              selectionMode="multiple"
              classNames={styles}
              onSelectionChange={(selectedItems: CustomerType[]) =>
                setSelectedCustomers(selectedItems)
              }
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="default" variant="flat" onPress={onClose}>
            {t("cancel")}
          </Button>
          <Button type="submit" className="bg-vistagreen text-white" onPress={handleSubmit}>
            {t("add")}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
